import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Circle,
  ClipboardPaste,
  Eraser,
  Minus,
  MousePointer2,
  Pencil,
  Scissors,
  Square,
  Trash2,
  Type,
  Undo2,
  X,
} from 'lucide-react';
import api from '../utils/api';
import { fileBasenameFromUrl } from '../utils/fileUrl';
import { canvasToPngFile, renderPdfPageToCanvas } from '../utils/floorPlanPdf';

const DEFAULT_W = 960;
const DEFAULT_H = 640;
const MAX_EDGE = 1200;
const ERASE_WIDTH = 22;

const TOOLS = [
  { id: 'select', label: 'Select', Icon: MousePointer2 },
  { id: 'pen', label: 'Pen', Icon: Pencil },
  { id: 'rect', label: 'Box', Icon: Square },
  { id: 'line', label: 'Straight', Icon: Minus },
  { id: 'circle', label: 'Circle', Icon: Circle },
  { id: 'text', label: 'Label', Icon: Type },
  { id: 'eraser', label: 'Eraser', Icon: Eraser },
];

let strokeSeq = 1;
function withId(stroke) {
  if (stroke?.id) return stroke;
  return { ...stroke, id: `sk-${Date.now()}-${strokeSeq++}` };
}

function strokeBounds(stroke) {
  if (!stroke) return null;
  if (stroke.type === 'pen' || stroke.type === 'eraser') {
    const pts = stroke.points || [];
    if (!pts.length) return null;
    let minX = pts[0][0];
    let maxX = pts[0][0];
    let minY = pts[0][1];
    let maxY = pts[0][1];
    pts.forEach(([x, y]) => {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });
    const pad = (stroke.width || 2.5) / 2 + 4;
    return { x: minX - pad, y: minY - pad, w: maxX - minX + pad * 2, h: maxY - minY + pad * 2 };
  }
  if (stroke.type === 'rect' || stroke.type === 'line' || stroke.type === 'select') {
    const x = Math.min(stroke.x1, stroke.x2);
    const y = Math.min(stroke.y1, stroke.y2);
    return {
      x,
      y,
      w: Math.abs(stroke.x2 - stroke.x1) || 1,
      h: Math.abs(stroke.y2 - stroke.y1) || 1,
    };
  }
  if (stroke.type === 'circle') {
    const r = Math.hypot(stroke.x2 - stroke.x1, stroke.y2 - stroke.y1);
    return { x: stroke.x1 - r, y: stroke.y1 - r, w: r * 2, h: r * 2 };
  }
  if (stroke.type === 'text') {
    const w = Math.max(40, String(stroke.text || '').length * 9);
    return { x: stroke.x, y: stroke.y - 16, w, h: 20 };
  }
  return null;
}

function pointInBounds(x, y, b, pad = 4) {
  if (!b) return false;
  return x >= b.x - pad && x <= b.x + b.w + pad && y >= b.y - pad && y <= b.y + b.h + pad;
}

function boundsIntersect(a, b) {
  if (!a || !b) return false;
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
}

function hitTestStroke(stroke, x, y) {
  return pointInBounds(x, y, strokeBounds(stroke), 6);
}

function drawStroke(ctx, stroke, { selected = false } = {}) {
  if (!stroke || stroke.type === 'select') return;
  ctx.save();
  if (stroke.type === 'eraser') {
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = '#ffffff';
    ctx.lineWidth = stroke.width || ERASE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (stroke.points?.length) {
      ctx.beginPath();
      stroke.points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.stroke();
    }
    ctx.restore();
    return;
  }

  ctx.strokeStyle = stroke.color || '#1e293b';
  ctx.fillStyle = stroke.color || '#1e293b';
  ctx.lineWidth = stroke.width || 2.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (stroke.type === 'pen' && stroke.points?.length) {
    ctx.beginPath();
    stroke.points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
    ctx.stroke();
  } else if (stroke.type === 'rect') {
    const x = Math.min(stroke.x1, stroke.x2);
    const y = Math.min(stroke.y1, stroke.y2);
    const w = Math.abs(stroke.x2 - stroke.x1);
    const h = Math.abs(stroke.y2 - stroke.y1);
    ctx.strokeRect(x, y, w, h);
  } else if (stroke.type === 'line') {
    ctx.beginPath();
    ctx.moveTo(stroke.x1, stroke.y1);
    ctx.lineTo(stroke.x2, stroke.y2);
    ctx.stroke();
  } else if (stroke.type === 'circle') {
    const r = Math.hypot(stroke.x2 - stroke.x1, stroke.y2 - stroke.y1);
    if (r > 0.5) {
      ctx.beginPath();
      ctx.arc(stroke.x1, stroke.y1, r, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else if (stroke.type === 'text' && stroke.text) {
    ctx.font = '600 16px system-ui, sans-serif';
    ctx.fillText(stroke.text, stroke.x, stroke.y);
  }
  ctx.restore();

  if (selected) {
    const b = strokeBounds(stroke);
    if (b) {
      ctx.save();
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(b.x - 2, b.y - 2, b.w + 4, b.h + 4);
      ctx.restore();
    }
  }
}

function paintAll(canvas, strokes, draft, bgImage, selectedIds) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (bgImage) {
    ctx.drawImage(bgImage, 0, 0, canvas.width, canvas.height);
  } else {
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    for (let x = 40; x < canvas.width; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 40; y < canvas.height; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
  }

  const sel = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  strokes.forEach((s) => drawStroke(ctx, s, { selected: sel.has(s.id) }));
  if (draft) {
    if (draft.type === 'select') {
      const x = Math.min(draft.x1, draft.x2);
      const y = Math.min(draft.y1, draft.y2);
      const w = Math.abs(draft.x2 - draft.x1);
      const h = Math.abs(draft.y2 - draft.y1);
      ctx.save();
      ctx.strokeStyle = '#2563eb';
      ctx.fillStyle = 'rgba(37, 99, 235, 0.12)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 4]);
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
    } else {
      drawStroke(ctx, draft);
    }
  }
}

async function fetchPlanBlob(planUrl) {
  const name = fileBasenameFromUrl(planUrl);
  if (!name) throw new Error('No drawing file name in URL');

  const paths = [`/files/view/${encodeURIComponent(name)}`, `/files/${encodeURIComponent(name)}`];
  const raw = String(planUrl || '').trim();
  if (/\/files\/(?:view\/)?[^/]+/i.test(raw)) {
    const apiPath = raw.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/?api/, '');
    if (apiPath.startsWith('/files/') && !paths.includes(apiPath.split('?')[0])) {
      paths.unshift(apiPath.split('?')[0]);
    }
  }

  let lastErr;
  for (const path of paths) {
    try {
      const res = await api.get(path, { responseType: 'blob', skipErrorToast: true });
      const ct = String(res.headers?.['content-type'] || '');
      if (ct.includes('application/json') || res.data?.type === 'application/json') {
        lastErr = new Error('File view returned JSON');
        continue;
      }
      if (!res.data || (res.data.size != null && res.data.size === 0)) {
        lastErr = new Error('Empty file');
        continue;
      }
      return { blob: res.data, name, contentType: ct || res.data.type || '' };
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Could not load drawing');
}

async function blobToBackground(blob, name, contentType) {
  const type = String(contentType || blob.type || '').toLowerCase();
  const isPdf = type.includes('pdf') || /\.pdf$/i.test(name || '');

  if (isPdf) {
    const file = new File([blob], name || 'plan.pdf', { type: 'application/pdf' });
    const { canvas } = await renderPdfPageToCanvas(file, 1, 1.25);
    return {
      image: canvas,
      w: canvas.width,
      h: canvas.height,
      objectUrl: null,
    };
  }

  const objectUrl = URL.createObjectURL(blob);
  const img = new Image();
  try {
    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Invalid or unsupported image'));
      img.src = objectUrl;
    });
    if (typeof img.decode === 'function') {
      try {
        await img.decode();
      } catch (_) {
        /* ignore */
      }
    }
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error('Image has no dimensions');
    return { image: img, w, h, objectUrl };
  } catch (e) {
    URL.revokeObjectURL(objectUrl);
    throw e;
  }
}

function fitSize(iw, ih) {
  const scale = Math.min(1, MAX_EDGE / Math.max(iw || 1, ih || 1));
  return {
    w: Math.max(320, Math.round(iw * scale)),
    h: Math.max(240, Math.round(ih * scale)),
  };
}

/**
 * Draw or edit a building sketch / annotate an uploaded plan.
 */
export default function BuildingSketchModal({
  title = 'Draw building sketch',
  backgroundUrl = null,
  onConfirm,
  onClose,
}) {
  const canvasRef = useRef(null);
  const bgRef = useRef(null);
  const blobUrlRef = useRef(null);
  const clipboardRef = useRef([]);
  const [clipboardCount, setClipboardCount] = useState(0);
  const [tool, setTool] = useState('pen');
  const [strokes, setStrokes] = useState([]);
  const [draft, setDraft] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [loadingBg, setLoadingBg] = useState(!!backgroundUrl);
  const [bgError, setBgError] = useState('');
  const [hasBg, setHasBg] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [bgReady, setBgReady] = useState(0);
  const drawing = useRef(false);
  const isEdit = !!backgroundUrl;

  useEffect(() => {
    let cancelled = false;
    bgRef.current = null;
    setHasBg(false);
    setBgError('');
    setStrokes([]);
    setDraft(null);
    setSelectedIds(new Set());

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (!backgroundUrl) {
      setCanvasSize({ w: DEFAULT_W, h: DEFAULT_H });
      setLoadingBg(false);
      setBgReady((n) => n + 1);
      return undefined;
    }

    setLoadingBg(true);
    (async () => {
      try {
        const { blob, name, contentType } = await fetchPlanBlob(backgroundUrl);
        if (cancelled) return;
        const loaded = await blobToBackground(blob, name, contentType);
        if (cancelled) {
          if (loaded.objectUrl) URL.revokeObjectURL(loaded.objectUrl);
          return;
        }
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = loaded.objectUrl;
        bgRef.current = loaded.image;
        setHasBg(true);
        setCanvasSize(fitSize(loaded.w, loaded.h));
        setBgReady((n) => n + 1);
      } catch (e) {
        if (!cancelled) {
          console.warn('[BuildingSketchModal] load background', backgroundUrl, e);
          setBgError(e?.response?.data?.error || e?.message || 'Could not load existing drawing');
          setHasBg(false);
          bgRef.current = null;
          setCanvasSize({ w: DEFAULT_W, h: DEFAULT_H });
          setBgReady((n) => n + 1);
        }
      } finally {
        if (!cancelled) setLoadingBg(false);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [backgroundUrl]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.width !== canvasSize.w || canvas.height !== canvasSize.h) {
      canvas.width = canvasSize.w;
      canvas.height = canvasSize.h;
    }
    paintAll(canvas, strokes, draft, bgRef.current, selectedIds);
  }, [strokes, draft, canvasSize, bgReady, selectedIds]);

  useLayoutEffect(() => {
    redraw();
  }, [redraw]);

  const canvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(strokes.map((s) => s.id).filter(Boolean)));
    setTool('select');
  }, [strokes]);

  const deleteSelected = useCallback(() => {
    if (!selectedIds.size) return;
    setStrokes((prev) => prev.filter((s) => !selectedIds.has(s.id)));
    setSelectedIds(new Set());
    setDraft(null);
  }, [selectedIds]);

  const cutSelected = useCallback(() => {
    if (!selectedIds.size) return;
    const cut = strokes.filter((s) => selectedIds.has(s.id));
    clipboardRef.current = cut.map((s) => ({ ...s }));
    setClipboardCount(cut.length);
    setStrokes((prev) => prev.filter((s) => !selectedIds.has(s.id)));
    setSelectedIds(new Set());
    setDraft(null);
  }, [selectedIds, strokes]);

  const pasteClipboard = useCallback(() => {
    const items = clipboardRef.current || [];
    if (!items.length) return;
    const offset = 16;
    const pasted = items.map((s) => {
      const copy = withId({ ...s, id: undefined });
      if (copy.type === 'pen' || copy.type === 'eraser') {
        copy.points = (copy.points || []).map(([x, y]) => [x + offset, y + offset]);
      } else if (copy.type === 'text') {
        copy.x = (copy.x || 0) + offset;
        copy.y = (copy.y || 0) + offset;
      } else {
        copy.x1 = (copy.x1 || 0) + offset;
        copy.y1 = (copy.y1 || 0) + offset;
        copy.x2 = (copy.x2 || 0) + offset;
        copy.y2 = (copy.y2 || 0) + offset;
      }
      return copy;
    });
    setStrokes((prev) => [...prev, ...pasted]);
    setSelectedIds(new Set(pasted.map((p) => p.id)));
    setTool('select');
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = String(e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selectAll();
      } else if (mod && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        cutSelected();
      } else if (mod && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteClipboard();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.size) {
          e.preventDefault();
          deleteSelected();
        }
      } else if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        setStrokes((prev) => prev.slice(0, -1));
        setSelectedIds(new Set());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectAll, cutSelected, pasteClipboard, deleteSelected, selectedIds]);

  const onPointerDown = (e) => {
    if (saving || loadingBg) return;
    const { x, y } = canvasPoint(e);
    if (tool === 'text') {
      const text = window.prompt('Label text (e.g. Lobby, Plant room)');
      if (text && text.trim()) {
        setStrokes((prev) => [...prev, withId({ type: 'text', x, y, text: text.trim(), color: '#0f172a' })]);
      }
      return;
    }

    if (tool === 'select') {
      drawing.current = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      // Click hit top-most stroke
      let hit = null;
      for (let i = strokes.length - 1; i >= 0; i--) {
        if (hitTestStroke(strokes[i], x, y)) {
          hit = strokes[i];
          break;
        }
      }
      if (hit && !e.shiftKey) {
        // start possible marquee only if drag; for now select hit
        setSelectedIds(new Set([hit.id]));
        setDraft({ type: 'select', x1: x, y1: y, x2: x, y2: y, mode: 'marquee' });
      } else if (hit && e.shiftKey) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(hit.id)) next.delete(hit.id);
          else next.add(hit.id);
          return next;
        });
        setDraft(null);
        drawing.current = false;
      } else {
        if (!e.shiftKey) setSelectedIds(new Set());
        setDraft({ type: 'select', x1: x, y1: y, x2: x, y2: y, mode: 'marquee' });
      }
      return;
    }

    drawing.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (tool === 'pen' || tool === 'eraser') {
      setDraft({
        type: tool === 'eraser' ? 'eraser' : 'pen',
        points: [[x, y]],
        color: tool === 'eraser' ? '#ffffff' : '#1e293b',
        width: tool === 'eraser' ? ERASE_WIDTH : 2.5,
      });
    } else {
      setDraft({ type: tool, x1: x, y1: y, x2: x, y2: y, color: '#1e293b', width: 2.5 });
    }
  };

  const onPointerMove = (e) => {
    if (!drawing.current || !draft) return;
    const { x, y } = canvasPoint(e);
    if (draft.type === 'pen' || draft.type === 'eraser') {
      setDraft({ ...draft, points: [...draft.points, [x, y]] });
    } else {
      setDraft({ ...draft, x2: x, y2: y });
    }
  };

  const finishStroke = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (!draft) return;

    if (draft.type === 'select') {
      const box = {
        x: Math.min(draft.x1, draft.x2),
        y: Math.min(draft.y1, draft.y2),
        w: Math.abs(draft.x2 - draft.x1),
        h: Math.abs(draft.y2 - draft.y1),
      };
      // Tiny drag = keep click selection already applied
      if (box.w > 4 || box.h > 4) {
        const ids = new Set();
        strokes.forEach((s) => {
          if (boundsIntersect(box, strokeBounds(s))) ids.add(s.id);
        });
        setSelectedIds(ids);
      }
      setDraft(null);
      return;
    }

    setStrokes((prev) => [...prev, withId(draft)]);
    setDraft(null);
    setSelectedIds(new Set());
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      paintAll(canvas, strokes, null, bgRef.current, new Set());
      const file = await canvasToPngFile(canvas, isEdit ? 'building-drawing-edit.jpg' : 'building-sketch.jpg');
      await onConfirm?.(file);
    } catch (err) {
      console.error('[BuildingSketchModal]', err);
      window.alert(err?.message || 'Could not save sketch');
    } finally {
      setSaving(false);
    }
  };

  const canSave = !loadingBg && (strokes.length > 0 || (isEdit && hasBg));
  const showCanvas = !loadingBg && (!isEdit || hasBg || !!bgError);
  const hasSelection = selectedIds.size > 0;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[95vh] overflow-auto flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isEdit
                ? 'Edit drawing — use Eraser to cover areas, Select + Delete/Cut for marks. Ctrl+A / Del / Ctrl+X / Ctrl+V.'
                : 'Sketch tools — Select, Eraser, Cut/Delete. Ctrl+A select all · Del delete · Ctrl+X cut · Ctrl+V paste.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-2 border-b flex flex-wrap items-center gap-2">
          {TOOLS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setTool(id);
                if (id !== 'select') setSelectedIds(new Set());
              }}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${
                tool === id
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
          <span className="w-px h-6 bg-gray-200 mx-1" />
          <button
            type="button"
            disabled={!strokes.length}
            onClick={selectAll}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 disabled:opacity-40"
            title="Ctrl+A"
          >
            Select all
          </button>
          <button
            type="button"
            disabled={!hasSelection}
            onClick={cutSelected}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 disabled:opacity-40"
            title="Ctrl+X"
          >
            <Scissors className="w-3.5 h-3.5" />
            Cut
          </button>
          <button
            type="button"
            disabled={!hasSelection}
            onClick={deleteSelected}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-700 disabled:opacity-40"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
          <button
            type="button"
            disabled={!clipboardCount}
            onClick={pasteClipboard}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 disabled:opacity-40"
            title="Ctrl+V"
          >
            <ClipboardPaste className="w-3.5 h-3.5" />
            Paste
          </button>
          <span className="w-px h-6 bg-gray-200 mx-1" />
          <button
            type="button"
            disabled={!strokes.length}
            onClick={() => {
              setStrokes((prev) => prev.slice(0, -1));
              setSelectedIds(new Set());
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 disabled:opacity-40"
          >
            <Undo2 className="w-3.5 h-3.5" />
            Undo
          </button>
          <button
            type="button"
            disabled={!strokes.length}
            onClick={() => {
              setStrokes([]);
              setDraft(null);
              setSelectedIds(new Set());
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 disabled:opacity-40"
          >
            Clear marks
          </button>
        </div>

        <div className="p-4 bg-slate-100 flex flex-col items-center justify-center overflow-auto min-h-[200px] gap-3">
          {loadingBg ? (
            <p className="text-sm text-gray-600 self-center py-16">Loading drawing…</p>
          ) : null}
          {!loadingBg && bgError ? (
            <p className="text-sm text-amber-800 self-center px-4 text-center max-w-lg">
              {bgError}. You can still draw a new sketch on a blank sheet, or re-upload the drawing.
            </p>
          ) : null}
          {showCanvas ? (
            <canvas
              ref={canvasRef}
              width={canvasSize.w}
              height={canvasSize.h}
              className={`bg-white border border-slate-300 rounded-lg shadow-sm max-w-full touch-none ${
                tool === 'select' ? 'cursor-default' : 'cursor-crosshair'
              }`}
              style={{ aspectRatio: `${canvasSize.w} / ${canvasSize.h}` }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={finishStroke}
              onPointerCancel={finishStroke}
              onPointerLeave={finishStroke}
            />
          ) : null}
          {hasSelection ? (
            <p className="text-[11px] text-slate-600">{selectedIds.size} selected — Cut / Delete / Paste available</p>
          ) : null}
        </div>

        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg border text-sm font-medium text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || !canSave}
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-40"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Use as drawing'}
          </button>
        </div>
      </div>
    </div>
  );
}
